import * as React from "react";
import Paper from "@mui/material/Paper";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Title from "@/components/molecules/Title";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { TitleGroup } from "@/components/molecules/TitleGroup";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { AuthButton } from "@/components/molecules/AuthButton.";
import { Grid, TableHead } from "@mui/material";
import {
  VehicleConstants,
  getGochargeCardLabel,
  getVehicleCodeAndEvCarLabel,
  getVehicleCodeLabel,
  lastCollectedAt,
} from "./VehicleConstants";
import { Car } from "@/models/vehicle";
import {
  getCarAPI,
  getLinkedDriverWorkCycles,
  getUploadedSettingFilesAPI,
} from "@/util/api/vehicle";
import { CommonConstants } from "@/common/CommonConstants";
import { DisplayName, GX_ADMIN } from "@/models/auth";
import { getJSTCurrentTime } from "@/util/datetime";
import { getLoginUserAPI } from "@/util/api/API";

export const VehicleDetail = () => {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const [car, setCar] = React.useState<Car>();
  const [settingFiles, setSettingFiles] = React.useState<string[]>([]);
  const [expired, setExpired] = React.useState(true);
  const [isGxAdmin, setIsGxAdmin] = React.useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // 一覧画面遷移時に、保存された一覧画面の検索条件を渡す
  const handleReturn = React.useCallback(
    () =>
      navigate("/cars", {
        state: location.state,
      }),
    [location.state, navigate]
  );

  // 編集画面遷移時に、一覧画面の検索条件を橋渡しする
  const handleEdit = React.useCallback(
    () =>
      navigate(`/cars/${id}/edit`, {
        state: location.state,
      }),
    [navigate, id, location.state]
  );

  const handleClickSettingFileRegister = () =>
    navigate(`/cars/${id}/setting_files`, {
      state: location.state,
    });

  React.useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        const { data: carInfo } = await getCarAPI(Number(id));
        const cycles =
          carInfo.evCars?.id != null
            ? await getLinkedDriverWorkCycles(carInfo.evCars.id)
            : null;

        if (cycles?.data.evCarId != null) {
          carInfo.evCars.frontDriverWorkCycle =
            cycles.data.frontDriverWorkCycles;
          carInfo.evCars.backDriverWorkCycle = cycles.data.backDriverWorkCycles;
        }

        if (ignore) {
          return;
        }

        setCar(carInfo);
        setExpired(
          carInfo.evCars?.usageEndOn != null &&
          new Date(carInfo.evCars.usageEndOn) <
          new Date(
            new Date(getJSTCurrentTime("date")).toISOString().slice(0, 10)
          )
        );
      } catch (e) {
        !ignore && console.log("==Error when get car==", e);
      }

      try {
        const { data: loginInfo } = await getLoginUserAPI();
        !ignore && setIsGxAdmin(loginInfo.role === GX_ADMIN);
      } catch (e) {
        !ignore &&
          console.log("===Error occurred while getting user info===", e);
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [id, location]);

  React.useEffect(() => {
    if (!car?.evCars?.id || !isGxAdmin) {
      return;
    }

    const getSettingFiles = async () => {
      try {
        const res = await getUploadedSettingFilesAPI(car.evCars.id);
        setSettingFiles(res.data);
      } catch (e) {
        console.error("===Error occurred while getting setting files===", e);
      }
    };

    getSettingFiles();
  }, [car?.evCars?.id, isGxAdmin]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>{VehicleConstants.Detail}</Title>
          <CommonButton
            btnType="Return"
            sx={{
              height: 35,
              mr: 1,
            }}
            onClickBtn={handleReturn}
          />
          <AuthButton
            label={CommonConstants.Edit}
            sx={{ backgroundColor: (theme) => theme.palette.primary.main, height: 35 }}
            name={DisplayName.CarsEdit}
            onClick={handleEdit}
            disabled={expired}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table className="gx-info-table">
                <TableHead>
                  <TableRow>
                    <TableCell className="body-none-custom">
                      <TitleGroup
                        style={{
                          fontWeight: "bold",
                          fontSize: "1.3rem",
                          paddingTop: 20,
                          marginBottom: 0,
                        }}
                        text={VehicleConstants.BasicInfomation}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{VehicleConstants.CompanyOffice}</TableCell>
                    <TableCell>{car?.office.displayName ?? ""}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.RadioNumber}</TableCell>
                    <TableCell>{getVehicleCodeLabel(car)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.PreviousCar}</TableCell>
                    <TableCell>
                      {getVehicleCodeAndEvCarLabel(car?.evCars?.previousCar)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.PlateNumber}</TableCell>
                    <TableCell>{car?.plateNumberFullText ?? ""}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.CarModelName}</TableCell>
                    <TableCell>{car?.carModels.displayName ?? ""}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.EVModel}</TableCell>
                    <TableCell>
                      {car?.evCars?.evCarModels?.name ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {VehicleConstants.ChargePlanningSystemUsageStartDate}
                    </TableCell>
                    <TableCell>
                      {car?.evCars?.usageStartOn?.replace(/-/g, "/") ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {VehicleConstants.ChargePlanningSystemUsageEndDate}
                    </TableCell>
                    <TableCell>
                      {car?.evCars?.usageEndOn?.replace(/-/g, "/") ?? ""}
                    </TableCell>
                  </TableRow>
                  {car?.evCars && isGxAdmin && (
                    <TableRow>
                      <TableCell>{VehicleConstants.NumberOfCars}</TableCell>
                      <TableCell>
                        {car?.evCars?.excludedFromNumberOfCars
                          ? VehicleConstants.Exclude
                          : VehicleConstants.Include}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>
                      {VehicleConstants.ChargeCardControlNumber}
                    </TableCell>
                    <TableCell>
                      {car?.evCars?.chargeCardEvs?.chargeCards
                        ?.managementNumber ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.ChargeCardRemarks}</TableCell>
                    <TableCell>
                      {car?.evCars?.chargeCardEvs?.cause ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.GochargeCard}</TableCell>
                    <TableCell>
                      {getGochargeCardLabel(car?.evCars?.gochargeCardEv)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.Subsidy}</TableCell>
                    <TableCell>
                      {car?.evCars?.subsidyType == null
                        ? ""
                        : String(car?.evCars?.subsidyType) === "0"
                          ? "利用制限なし"
                          : "GI基金"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table className="gx-info-table">
                <TableHead>
                  <TableRow>
                    <TableCell className="body-none-custom">
                      <TitleGroup
                        style={{
                          paddingTop: 25,
                          marginBottom: 0,
                        }}
                        text={VehicleConstants.SocAcquisitionInformation}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{VehicleConstants.IMEI}</TableCell>
                    <TableCell>{car?.evCars?.imei ?? ""}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.IMEI2}</TableCell>
                    <TableCell>{car?.evCars?.imei2 ?? ""}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.VIN}</TableCell>
                    <TableCell>{car?.evCars?.vin ?? ""}</TableCell>
                  </TableRow>
                  {isGxAdmin && (
                    <TableRow>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {VehicleConstants.SettingFile}
                      </TableCell>
                      <TableCell sx={{ py: 0 }}>
                        <SettingFileList
                          disabled={car?.evCars?.id == null}
                          settingFiles={settingFiles}
                          onClickRegister={handleClickSettingFileRegister}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Table className="gx-info-table">
                <TableHead>
                  <TableRow>
                    <TableCell className="body-none-custom">
                      <TitleGroup
                        style={{
                          paddingTop: 25,
                          marginBottom: 0,
                        }}
                        text={VehicleConstants.SocInformation}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {VehicleConstants.ResidualPowerPercent}
                    </TableCell>
                    <TableCell>
                      {car?.evCars?.evCarSoc?.socPercent != null
                        ? `${car.evCars.evCarSoc.socPercent}${VehicleConstants.Percent}`
                        : ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.SocLastCollectedAt}</TableCell>
                    <TableCell>
                      {lastCollectedAt(
                        "full",
                        car?.evCars?.evCarSoc?.lastCollectedAt
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table className="gx-info-table">
                <TableHead>
                  <TableRow>
                    <TableCell className="body-none-custom">
                      <TitleGroup
                        style={{
                          paddingTop: 25,
                          marginBottom: 0,
                        }}
                        text={VehicleConstants.WorkCycle}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{VehicleConstants.FrontApplyOn}</TableCell>
                    <TableCell>
                      {car?.evCars?.frontDriverWorkCycleApplyOn?.replace(
                        /-/g,
                        "/"
                      ) ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.Front}</TableCell>
                    <TableCell>
                      {car?.evCars?.frontDriverWorkCycle?.shortName ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.FrontBaseDate}</TableCell>
                    <TableCell>
                      {car?.evCars?.frontDriverWorkCycleBaseDate?.replace(
                        /-/g,
                        "/"
                      ) ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.Rear}</TableCell>
                    <TableCell>
                      {car?.evCars?.backDriverWorkCycle?.shortName ?? ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{VehicleConstants.BackBaseDate}</TableCell>
                    <TableCell>
                      {car?.evCars?.backDriverWorkCycleBaseDate?.replace(
                        /-/g,
                        "/"
                      ) ?? ""}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {car?.evCars && isGxAdmin && (
                <Table className="gx-info-table">
                  <TableHead>
                    <TableRow>
                      <TableCell className="body-none-custom">
                        <TitleGroup
                          style={{
                            paddingTop: 25,
                            marginBottom: 0,
                          }}
                          text={VehicleConstants.ParkingLot}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{VehicleConstants.ParkingLot}</TableCell>
                      <TableCell>
                        {car.evCars.officeLocations?.name ?? ""}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

type SettingFileListProps = {
  disabled?: boolean;
  settingFiles: string[];
  onClickRegister: () => void;
};
const SettingFileList = ({
  disabled,
  settingFiles,
  onClickRegister,
}: SettingFileListProps) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    {settingFiles.length > 0 ? (
      <ul style={{ padding: 0 }}>
        {settingFiles.map((file) => (
          <li key={file} style={{ listStyle: "none" }}>
            {file}
          </li>
        ))}
      </ul>
    ) : (
      <div></div>
    )}
    <CommonButton
      btnType="Replace"
      sx={{ height: 35 }}
      onClickBtn={onClickRegister}
      disabled={disabled}
    />
  </div>
);
